import { DOCUMENT } from '@angular/common'
import { inject, WritableSignal } from '@angular/core'
import { WA_WINDOW } from '@ng-web-apis/common'

export const MOBILE_BREAKPOINT = 768

/**
 * Returns the window object safely via the `WA_WINDOW` token or `DOCUMENT.defaultView` fallback.
 * Must be called in an injection context.
 */
export function injectWindow(): Window | null {
    try {
        return inject(WA_WINDOW)
    } catch {
        const doc = inject(DOCUMENT, { optional: true })
        return doc?.defaultView ?? null
    }
}

/**
 * Checks whether the specified window width is less than or equal to the breakpoint.
 */
export function isMobileScreen(
    windowRef: Window | null | undefined,
    breakpoint: number = MOBILE_BREAKPOINT,
): boolean {
    if (!windowRef) {
        return false
    }
    return windowRef.innerWidth <= breakpoint
}

/**
 * Injects the window reference and returns a predicate function `() => boolean`
 * that evaluates whether the current viewport is mobile.
 *
 * Must be called in an injection context (e.g. property initializer or constructor).
 */
export function injectIsMobile(
    breakpoint: number = MOBILE_BREAKPOINT,
): () => boolean {
    const windowRef = injectWindow()
    return () => isMobileScreen(windowRef, breakpoint)
}

/**
 * Toggles either the mobile sidebar signal or desktop collapsed sidebar signal
 * based on whether the current viewport is within the mobile breakpoint.
 */
export function toggleResponsiveSidebar(
    isMobile: boolean,
    mobileSidebarSignal: WritableSignal<boolean>,
    desktopCollapsedSignal: WritableSignal<boolean>,
): void {
    if (isMobile) {
        mobileSidebarSignal.update((v) => !v)
    } else {
        desktopCollapsedSignal.update((v) => !v)
    }
}
